import { AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, Input, LOCALE_ID, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Subscription, catchError, map, switchMap, tap } from 'rxjs';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DateUtility } from '../../../date.utility';
import { DateFormatType } from '../../../enums/date-format-type.enum';
import { ContractDetail, Payment } from '../../../models/contract-model';
import { PaymentProps } from '../../models/payment-props.enum';
import { paymentFormValidator } from '../../../validators/payment-form.validator';
import { select, Store } from '@ngrx/store';
import { getCustomer } from '../../../store/domains/customer/customer.selectors';
import { Customer } from '../../../models/consumer';
import { FeatureToggleService } from '../../../services/feature-toggle.service';
import { CPEnvironment } from '../../../models';
import { ConsumerPortalEnviromentConfig } from '../../../models/environment-consumer-portal';
import { PaymentMinMaxBoundary } from '../../models/payment-data.model';
import { VersionToggleService } from '../../../services/version-toggle';
import { AppFacadeService } from '../../../services/app-facade.service';
import { AppMediatorService } from '../../../services/app-mediator.service';
import { ContractService } from '../../../services/contract.service';
import { PayMinimumAmount } from '../../../models/pay-minimum-amount.model';
import { PaymentTransactionType } from '../../../enums/payment-method.enum';
import { splitCurrencyAmount } from '../../../utils/string.util';

@Component({
    standalone: false,
    selector: 'por-payment-page',
    templateUrl: './por-payment-page.component.html',
    styleUrls: ['./por-payment-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PorPaymentPageComponent implements OnInit, OnDestroy, AfterViewInit {
    readonly appFacadeService = inject(AppFacadeService);
    private readonly translateService = inject(TranslateService);
    private readonly appMediatorService = inject(AppMediatorService);
    private readonly store = inject(Store);
    private readonly featureToggleService = inject(FeatureToggleService);
    versionToggle = inject(VersionToggleService);
    readonly contractService = inject(ContractService);
    private readonly appConfig = inject<ConsumerPortalEnviromentConfig>(CPEnvironment);

    constructor() {
        const locale = inject(LOCALE_ID);

        this.dateFormat = DateUtility.getDateDisplayFormat(DateFormatType.StandardDate, locale);
    }

    customer!: Customer;
    @Input() enableBackButton = false;
    @Input() customerEmail!: string | undefined;
    @Input() customerId = '';
    @Input() contract!: ContractDetail;
    @Input() organizationId!: string;
    isTransactionGoing = false;
    @Output() readonly appOutput = new EventEmitter();
    @Output() readonly paymentSuccess: EventEmitter<Payment> = new EventEmitter<Payment>();
    dateFormat!: string;
    featureService = this.featureToggleService;
    porPayURL = this.appConfig.porPayScriptURL;

    private readonly subscriptions: Subscription[] = [];
    apiUrl = this.appConfig.gapiApiBaseUrl;
    paymentAppInput$: BehaviorSubject<string> = new BehaviorSubject('');
    apiKey = '';
    @Output() readonly closePanel = new EventEmitter<boolean>();

    paymentBoundaryMinMax!: PaymentMinMaxBoundary[];
    minimumPaymentDue = 0;
    maxPaymentDue = 0;
    isDialogEnabled = false;
    dialogMessage = '';
    isNoPaymentBoundaryFound = new BehaviorSubject<boolean>(false);
    payMinimumAmountText = '';

    form: FormGroup = new FormGroup(
        {
            paymentType: new FormControl(PaymentProps.TYPE_FULL_AMOUNT_DUE, {
                validators: [Validators.required]
            }),
            amount: new FormControl('')
        },
        {
            validators: [paymentFormValidator()]
        }
    );
    private readonly isPaymentBoundaryAvailable = new BehaviorSubject<boolean>(false);
    isPaymentBoundaryAvailable$ = this.isPaymentBoundaryAvailable.pipe(
        tap(() => {
            this.appFacadeService.setLoading(true);
        }),
        switchMap(() => {
            return this.appMediatorService.porPayService.getMinMaxFromPaymentBoundary(this.customerId, this.contract.Id).pipe(
                map((res: PaymentMinMaxBoundary[]) => {
                    this.appFacadeService.setLoading(false);
                    if (res.length === 0) {
                        this.isNoPaymentBoundaryFound.next(true);
                        return false;
                    }
                    this.paymentBoundaryMinMax = res;
                    if (this.showPaymentRadioButtons(PaymentProps.TYPE_MIN_AMOUNT_DUE)) {
                        this.form.get('paymentType')?.setValue(PaymentProps.TYPE_MIN_AMOUNT_DUE);
                    }
                    return true;
                }),
                catchError(e => {
                    this.appFacadeService.setLoading(false);
                    throw new Error(this.translateService.instant('somethingWentWrong', e));
                })
            );
        })
    );

    ngOnInit(): void {
        const lang = this.appMediatorService.localStorageService.selectedContentLanguage;
        if (lang && lang !== this.translateService.getCurrentLang()) {
            this.translateService.use(lang);
        }
        this.subscriptions.push(
            this.store.pipe(select(getCustomer)).subscribe((customer: Customer) => {
                this.customer = customer;
            })
        );
        this.subscriptions.push(
            this.appFacadeService.getConfigPayMinimumAmount().subscribe((value: PayMinimumAmount) => {
                this.payMinimumAmountText = value.translate ? this.translateService.instant(value.text) : value.text ? value.text : this.translateService.instant('PayMinimumAmountDue');
            })
        );
        this.appFacadeService.setSelectedContracts([this.contract.Id]);
    }

    ngAfterViewInit(): void {
        this.subscriptions.push(
            this.form.valueChanges.pipe().subscribe(val => {
                if (val?.paymentType !== PaymentProps.TYPE_OTHER_AMOUNT && this.form.controls?.['amount'].value !== '') {
                    this.form.controls?.['amount'].setValue('');
                    this.form.controls?.['amount'].updateValueAndValidity();
                }
            })
        );
    }

    private validatePayOtherOption(min: number, max: number) {
        /**
         * IF min is null/0 then the min Amount is 1.
         */
        this.form.controls['amount'].setValidators([Validators.min(min ?? 1), Validators.max(max ?? this.contract.AmountDue)]);
        this.form.controls['amount'].updateValueAndValidity();
    }

    createTransaction(): void {
        if (this.form.valid && this.customerId) {
            let amountBeingPaidBeforeFee: number = this.maxPaymentDue ?? this.contract.AmountDue;
            if (this.form.get('paymentType')?.value === PaymentProps.TYPE_OTHER_AMOUNT) {
                amountBeingPaidBeforeFee = Number(this.form.get('amount')?.value);
            } else if (this.form.get('paymentType')?.value === PaymentProps.TYPE_MIN_AMOUNT_DUE) {
                amountBeingPaidBeforeFee = this.minimumPaymentDue;
            }

            this.appFacadeService.createTransaction(amountBeingPaidBeforeFee.toString(), PaymentTransactionType.Single);
        }
    }

    selectOtherType(): void {
        this.form.get('paymentType')?.setValue(PaymentProps.TYPE_OTHER_AMOUNT);
    }

    ngOnDestroy(): void {
        this.subscriptions.map(sub => sub.unsubscribe());
    }

    /**
     * Show Payment Radio Buttons
     * @param {PaymentProps} button
     * @returns
     */
    showPaymentRadioButtons(button: PaymentProps): boolean {
        const { min, max } = this.filterPaymentBoundary();
        this.validatePayOtherOption(min, max);
        switch (button) {
            case PaymentProps.TYPE_FULL_AMOUNT_DUE:
                if (this.featureService.isAvailable('payFullAmount')) {
                    // If min and max < 1, then hide it and show exeception message
                    if (min < 1 && max < 1) {
                        return false;
                    }
                    return true;
                }
                return false;
            case PaymentProps.TYPE_OTHER_AMOUNT:
                // if both min and max are same, We want to hide Pay Other Amount button
                if (min > 0 && max > 0 && min === max) {
                    return false;
                }
                return (min < this.contract.AmountDue && max > this.contract.AmountDue) || max <= this.contract.AmountDue ? true : false;
            case PaymentProps.TYPE_MIN_AMOUNT_DUE:
                if (min === 0 || min === 1 || min === max) {
                    return false;
                }
                return (min < this.contract.AmountDue && max > this.contract.AmountDue) || max <= this.contract.AmountDue ? true : false;
            default:
                return false;
        }
    }

    filterPaymentBoundary(): { min: number; max: number } {
        /**
         * Initilize min and max with default non zero value
         */
        let min = this.paymentBoundaryMinMax[0].minAmount;
        let max = this.paymentBoundaryMinMax[0].maxAmount;
        this.paymentBoundaryMinMax.map((boundary: PaymentMinMaxBoundary) => {
            if (boundary.minAmount < min) {
                min = boundary.minAmount;
            }
            if (boundary.maxAmount > max) {
                max = boundary.maxAmount;
            }
        });
        /**
         * IF min is 0 then the min Amount is 1.
         */
        this.minimumPaymentDue = min <= 0 ? 1 : min;
        this.maxPaymentDue = max;

        return { min: min, max: max };
    }

    /**
     * To show exeception message, none of payment option is available
     */
    get isAnyPaymentOptionVisible(): boolean {
        return this.showPaymentRadioButtons(1) || this.showPaymentRadioButtons(2) || this.showPaymentRadioButtons(3);
    }

    get isPaymentIncludeDeposit(): boolean {
        return this.maxPaymentDue > Number(this.contract.GrandTotal);
    }

    get depositAmount() {
        return this.maxPaymentDue > Number(this.contract.GrandTotal) ? this.maxPaymentDue - Number(this.contract.GrandTotal) : 0;
    }

    get totalAmountDue() {
        return this.maxPaymentDue > Number(this.contract.GrandTotal) ? this.maxPaymentDue : this.contract.GrandTotal;
    }

    closeButtonPanel() {
        this.closePanel.emit();
    }

    /**
     * It separates USD(CurrencyCode) from Amount and wrap it separately
     * @param amount
     * @returns string
     */
    formatAmount(amount: string) {
        return splitCurrencyAmount(amount);
    }
}
